import {
  Appearance,
  AppearanceUpdateRequest,
  BootstrapResponse,
  CatalogPurchaseRequest,
  CatalogPurchaseResponse,
  CatalogResponse,
  ClientAnalyticsBatch,
  DecorationPlaceRequest,
  DecorationRemoveRequest,
  HomeLookUpdateRequest,
  HomeAccessRequest,
  HomeStateResponse,
  HomeUpgradeRequest,
  HomeUpgradeResponse,
  InventoryResponse,
  MyHomeResponse,
  ResetLayoutRequest,
  InteriorBoardUpdateRequest,
  InteriorBoardUpdateResponse,
  InteriorThemeUpdateRequest,
  InteriorThemeUpdateResponse,
  OrderCreateRequest,
  OrderListResponse,
  OrderView,
  OutboundIntentRequest,
  OutboundIntentResponse,
  PaymentIntentResponse,
  PaymentsConfigResponse,
  QuoteRequest,
  QuoteResponse,
  PlotPurchaseRequest,
  PlotPurchaseResponse,
  PresenceResponse,
  PropertyAccessUpdateRequest,
  PropertyAccessUpdateResponse,
  PropertyHistoryResponse,
  PropertyListResponse,
  PropertyPublic,
  PropertyStatsResponse,
  RealEstateListingsResponse,
  RoomTicketRequest,
  RoomTicketResponse,
  SendVisitorsOutResponse,
  StatsOverviewResponse,
  WalletHistoryResponse,
  WalletsResponse,
  WorldManifest,
  type AppearanceChoice,
  type ClientAnalyticsEvent,
  type RoomTarget,
} from "@townsy/contracts";
import { z } from "zod";
import { apiFetch } from "./client";

/** Typed, contract-validated calls to the versioned API (docs/06). */

export const fetchBootstrap = (signal?: AbortSignal) =>
  apiFetch("/api/v1/bootstrap", { schema: BootstrapResponse, signal });

export const fetchWorldManifest = () =>
  apiFetch("/api/v1/world/manifest", { schema: z.object({ manifest: WorldManifest }) });

export const fetchProperties = (filter: { districtId?: string; type?: "COMMERCIAL" | "RESIDENTIAL" } = {}) => {
  const q = new URLSearchParams();
  if (filter.districtId) q.set("districtId", filter.districtId);
  if (filter.type) q.set("type", filter.type);
  const qs = q.toString();
  return apiFetch(`/api/v1/properties${qs ? `?${qs}` : ""}`, { schema: PropertyListResponse });
};

export const fetchProperty = (id: string) => apiFetch(`/api/v1/properties/${encodeURIComponent(id)}`, { schema: PropertyPublic });

export const fetchPropertyHistory = (id: string) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(id)}/history`, { schema: PropertyHistoryResponse });

/** Properties whose next stats read must not come from the browser's HTTP cache (the route sends max-age=30). */
const staleStats = new Set<string>();

/** After a purchase or an ownership change: the next `fetchPropertyStats(id)` revalidates with the server. */
export function markPropertyStatsStale(id: string): void {
  staleStats.add(id);
}

export const fetchPropertyStats = async (id: string) => {
  const fresh = staleStats.has(id);
  const res = await apiFetch(`/api/v1/properties/${encodeURIComponent(id)}/stats`, { schema: PropertyStatsResponse, ...(fresh ? { cache: "no-cache" as const } : {}) });
  if (fresh) staleStats.delete(id);
  return res;
};

export const fetchStatsOverview = () => apiFetch("/api/v1/stats/overview", { schema: StatsOverviewResponse });

export const fetchPresence = () => apiFetch("/api/v1/stats/presence", { schema: PresenceResponse });

/** Private profile summary; the exact shape is finalised in M2, so only well-known keys are read. */
export const MeResponse = z
  .object({
    userId: z.string().optional(),
    email: z.string().optional(),
    emailVerified: z.boolean().optional(),
    displayName: z.string().nullable().optional(),
    roles: z.array(z.string()).optional(),
  })
  .loose();
export type MeResponse = z.infer<typeof MeResponse>;
export const fetchMe = () => apiFetch("/api/v1/me", { schema: MeResponse });

export const postRoomTicket = (target: RoomTarget) =>
  apiFetch("/api/v1/room-tickets", {
    method: "POST",
    body: RoomTicketRequest.parse({ target }),
    schema: RoomTicketResponse,
  });

export const postOutboundIntent = (propertyId: string) =>
  apiFetch("/api/v1/outbound-intents", {
    method: "POST",
    body: OutboundIntentRequest.parse({ propertyId }),
    schema: OutboundIntentResponse,
  });

// ---------------------------------------------------------------- wallets (residents only; 401 for guests)

/** Coins and dollar Credit: two separate journal-backed balances, never summed (docs/04 section 1). */
export const fetchWallets = () => apiFetch("/api/v1/me/wallets", { schema: WalletsResponse });

export type WalletUnit = "COIN" | "CREDIT_USD";
export const fetchWalletHistory = (unit: WalletUnit, cursor?: string | null) => {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return apiFetch(`/api/v1/me/wallets/${unit}/history${qs}`, { schema: WalletHistoryResponse });
};

// ---------------------------------------------------------------- Real Estate office

export const fetchRealEstateListings = () => apiFetch("/api/v1/real-estate/listings", { schema: RealEstateListingsResponse });

/**
 * Buy an available residential plot with coins. `idempotencyKey` must be generated once per
 * confirmation and reused verbatim on a retry of the same request (docs/04 section 6): the server
 * answers a repeated key + body with the original result, so a retry can never spend twice.
 */
export const postPlotPurchase = (propertyId: string, body: PlotPurchaseRequest, idempotencyKey: string) =>
  apiFetch(`/api/v1/real-estate/plots/${encodeURIComponent(propertyId)}/purchase`, {
    method: "POST",
    body: PlotPurchaseRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: PlotPurchaseResponse,
  });

// ---------------------------------------------------------------- homes: catalog, upgrades, inventory, layout (ADR-0005)

/** Public: home levels with their upgrade offers and every yard/furniture item with its offer. */
export const fetchCatalog = () => apiFetch("/api/v1/catalog", { schema: CatalogResponse });

/** Buy ONE item with coins (it goes to storage). Same Idempotency-Key rules as the plot purchase. */
export const postCatalogPurchase = (itemId: string, body: CatalogPurchaseRequest, idempotencyKey: string) =>
  apiFetch(`/api/v1/catalog/items/${encodeURIComponent(itemId)}/purchase`, {
    method: "POST",
    body: CatalogPurchaseRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: CatalogPurchaseResponse,
  });

/** Upgrade the owner's home to exactly the next level, paid with coins. */
export const postHomeUpgrade = (propertyId: string, body: HomeUpgradeRequest, idempotencyKey: string) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/upgrade`, {
    method: "POST",
    body: HomeUpgradeRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: HomeUpgradeResponse,
  });

/** Free look choices (style, colours, floor); 409 PROPERTY_VERSION_CHANGED when the layout moved on. */
export const patchHomeLook = (propertyId: string, body: HomeLookUpdateRequest) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/look`, {
    method: "PATCH",
    body: HomeLookUpdateRequest.parse(body),
    schema: HomeStateResponse,
  });

/** ADR-0019: open or close the home to visitors (closing moves nobody out); bumps the layout version. */
export const patchHomeAccess = (propertyId: string, body: HomeAccessRequest) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/access`, {
    method: "PATCH",
    body: HomeAccessRequest.parse(body),
    schema: HomeStateResponse,
  });

export const postDecorationPlace = (propertyId: string, body: DecorationPlaceRequest) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/decorations/place`, {
    method: "POST",
    body: DecorationPlaceRequest.parse(body),
    schema: HomeStateResponse,
  });

/** Returns the item to storage (never deletes it). */
export const postDecorationRemove = (propertyId: string, body: DecorationRemoveRequest) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/decorations/remove`, {
    method: "POST",
    body: DecorationRemoveRequest.parse(body),
    schema: HomeStateResponse,
  });

/** Every placed item (yard and inside) goes back to storage; nothing is deleted. */
export const postResetLayout = (propertyId: string, body: ResetLayoutRequest) =>
  apiFetch(`/api/v1/homes/${encodeURIComponent(propertyId)}/reset-layout`, {
    method: "POST",
    body: ResetLayoutRequest.parse(body),
    schema: HomeStateResponse,
  });

export const fetchInventory = () => apiFetch("/api/v1/me/inventory", { schema: InventoryResponse });

/** The signed-in resident's home (`home: null` when they own no plot). */
export const fetchMyHome = () => apiFetch("/api/v1/me/home", { schema: MyHomeResponse });

// ---------------------------------------------------------------- store board / permissions

export const MyPermissionsResponse = z.object({ isOwner: z.boolean(), canEditBoard: z.boolean() });
export type MyPermissionsResponse = z.infer<typeof MyPermissionsResponse>;
export const fetchMyPermissions = (propertyId: string) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(propertyId)}/my-permissions`, { schema: MyPermissionsResponse });

export const patchInteriorBoard = (propertyId: string, body: InteriorBoardUpdateRequest) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(propertyId)}/interior-board`, {
    method: "PATCH",
    body: InteriorBoardUpdateRequest.parse(body),
    schema: InteriorBoardUpdateResponse,
  });

/**
 * Choose the store's interior theme (ADR-0006 #5): the current commercial owner only, free, audited.
 * 401 guests, 403 non-owners, 422 VALIDATION_FAILED / PROPERTY_TYPE_NOT_ELIGIBLE, 429 rate limit;
 * the same theme is a no-op. Visitors inside see the change through `property.changed`.
 */
export const putInteriorTheme = (propertyId: string, body: InteriorThemeUpdateRequest) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(propertyId)}/interior-theme`, {
    method: "PUT",
    body: InteriorThemeUpdateRequest.parse(body),
    schema: InteriorThemeUpdateResponse,
  });

/** ADR-0019: open or close a store to visitors (current owner; closing moves nobody out). */
export const putStoreAccess = (propertyId: string, body: PropertyAccessUpdateRequest) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(propertyId)}/access`, {
    method: "PUT",
    body: PropertyAccessUpdateRequest.parse(body),
    schema: PropertyAccessUpdateResponse,
  });

/** ADR-0019: the owner of a home or store asks everyone else inside to leave (the room moves them to the street). */
export const postSendVisitorsOut = (propertyId: string) =>
  apiFetch(`/api/v1/properties/${encodeURIComponent(propertyId)}/send-visitors-out`, {
    method: "POST",
    schema: SendVisitorsOutResponse,
  });

// ---------------------------------------------------------------- dollar commerce (M4, ADR-0007; Stripe TEST mode)

/** Public: whether dollar purchases are available, the provider, the publishable key and the test-mode notice. */
export const fetchPaymentsConfig = () => apiFetch("/api/v1/payments/config", { schema: PaymentsConfigResponse });

/** A server-priced, single-use quote (expires after ~120 s). The browser never sends a price. */
export const postQuote = (body: QuoteRequest) =>
  apiFetch("/api/v1/quotes", { method: "POST", body: QuoteRequest.parse(body), schema: QuoteResponse });

/**
 * `POST /orders` answers `{ order, ... }` (the order view plus server-only extras); older drafts
 * answered the bare OrderView. Both are accepted; an inline PaymentIntent (if the API ever returns
 * one) is used instead of a second call.
 */
const OrderCreateResponseLoose = z.union([
  z
    .object({
      order: OrderView,
      paymentIntent: PaymentIntentResponse.nullable().optional(),
    })
    .loose(),
  OrderView,
]);
export interface OrderCreateResult {
  order: OrderView;
  paymentIntent: PaymentIntentResponse | null;
}

/**
 * Create (or, with the same Idempotency-Key and quote, re-read) the order for a quote. The key is
 * generated once per confirmation and reused verbatim on retries, so a lost response can never
 * create a second order or hold.
 */
export const postOrder = async (quoteId: string, idempotencyKey: string): Promise<OrderCreateResult> => {
  const res = await apiFetch("/api/v1/orders", {
    method: "POST",
    body: OrderCreateRequest.parse({ quoteId }),
    headers: { "idempotency-key": idempotencyKey },
    schema: OrderCreateResponseLoose,
  });
  if ("order" in res && typeof res.order === "object") {
    return { order: res.order, paymentIntent: (res as { paymentIntent?: PaymentIntentResponse | null }).paymentIntent ?? null };
  }
  return { order: res as OrderView, paymentIntent: null };
};

/** Create or reuse this buyer's PaymentIntent for the order's fixed card amount (client secret: never logged). */
export const postPaymentIntent = (orderId: string) =>
  apiFetch(`/api/v1/orders/${encodeURIComponent(orderId)}/payment-intent`, { method: "POST", body: {}, schema: PaymentIntentResponse });

/** The buyer's own order (= receipt): status, funding, payment and refund state. */
export const fetchOrder = (orderId: string, signal?: AbortSignal) =>
  apiFetch(`/api/v1/orders/${encodeURIComponent(orderId)}`, { schema: OrderView, signal });

export const fetchMyOrders = (cursor?: string | null) => {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return apiFetch(`/api/v1/me/orders${qs}`, { schema: OrderListResponse });
};

const OrderCancelResponseLoose = z.union([z.object({ order: OrderView }).loose(), OrderView]);
/**
 * Ask to cancel (a request: the payment provider's actual state decides — a cancel that loses the
 * race completes the purchase). Resolves with the order as the server sees it afterwards.
 */
export const postOrderCancel = async (orderId: string): Promise<OrderView> => {
  const res = await apiFetch(`/api/v1/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: "POST",
    body: {},
    schema: OrderCancelResponseLoose,
  });
  return "order" in res && typeof res.order === "object" ? res.order : (res as OrderView);
};

/**
 * LOCAL DEVELOPMENT ONLY: with `PAYMENTS_PROVIDER=simulated` (refused on staging/production) the API
 * mounts this route to play the buyer's card confirmation, because Stripe.js cannot confirm a
 * simulated payment. The UI offers it only when `/payments/config` reports provider "simulated".
 */
export const postSimulatePayment = (orderId: string, outcome: "succeed" | "decline") =>
  apiFetch(`/api/v1/dev/orders/${encodeURIComponent(orderId)}/simulate-payment`, { method: "POST", body: { outcome }, schema: OrderView });

// ---------------------------------------------------------------- notifications (docs/06 section 2)

/** Account-scoped messages (`property.displaced`, `order.fulfilled`, ...); payloads are validated per type by the reader. */
export const NotificationItem = z
  .object({
    id: z.string(),
    type: z.string(),
    payload: z.unknown(),
    createdAt: z.string().optional(),
    readAt: z.string().nullable().optional(),
  })
  .loose();
export type NotificationItem = z.infer<typeof NotificationItem>;
export const NotificationListResponse = z.object({ items: z.array(NotificationItem), nextCursor: z.string().nullable().optional() }).loose();
export type NotificationListResponse = z.infer<typeof NotificationListResponse>;

export const fetchNotifications = () => apiFetch("/api/v1/me/notifications", { schema: NotificationListResponse });

export const postNotificationRead = (id: string) =>
  apiFetch(`/api/v1/me/notifications/${encodeURIComponent(id)}/read`, { method: "POST", body: {} });

// ---------------------------------------------------------------- avatar

export const AppearanceUpdateResponse = z.object({ appearance: Appearance });
export const putAppearance = (choice: AppearanceChoice) =>
  apiFetch("/api/v1/me/appearance", {
    method: "PUT",
    body: AppearanceUpdateRequest.parse(choice),
    schema: AppearanceUpdateResponse,
  });

export const AnalyticsAccepted = z.object({ accepted: z.number().int(), deduped: z.number().int() });
export const postAnalyticsBatch = (events: ClientAnalyticsEvent[], opts: { keepalive?: boolean } = {}) =>
  apiFetch("/api/v1/analytics/events", {
    method: "POST",
    body: ClientAnalyticsBatch.parse({ events }),
    schema: AnalyticsAccepted,
    keepalive: opts.keepalive,
  });
